import type { UUID } from '@aline/common';

export interface RequesterFieldCounts {
  completed: number;
  total: number;
}

/** What tapping the walkthrough's Next / Finish control should do. */
export type WalkthroughAdvance =
  | { type: 'finish' }
  | { type: 'focusNextField' }
  | { documentId: UUID; type: 'navigateToDocument' }
  | { type: 'noNextDocument' }
  | { type: 'none' };

interface WalkthroughAdvanceInput {
  /** The Finish action is only reachable from controls that can execute it. */
  canFinish: boolean;
  currentDocumentId: UUID;
  isFieldWalkthroughFinished: boolean;
  /** False for a standalone document, where there is nowhere else to advance to. */
  isInWorkflow: boolean;
  orderedFieldsInCurrentDoc: { isComplete: boolean }[];
  orderedWalkthroughDocuments: { id: UUID; requesterFields: { isComplete: boolean }[] }[];
  requesterFieldCounts: RequesterFieldCounts;
}

export function getWalkthroughAdvance({
  canFinish,
  currentDocumentId,
  isFieldWalkthroughFinished,
  isInWorkflow,
  orderedFieldsInCurrentDoc,
  orderedWalkthroughDocuments,
  requesterFieldCounts,
}: WalkthroughAdvanceInput): WalkthroughAdvance {
  // Order matters — finishing wins over every other outcome.
  if (isFieldWalkthroughFinished && canFinish) return { type: 'finish' };

  const isUserFinishedWithCurrentDoc = orderedFieldsInCurrentDoc.every(
    ({ isComplete }) => isComplete
  );
  if (!isUserFinishedWithCurrentDoc || !isInWorkflow) return { type: 'focusNextField' };

  const nextDocWithIncompleteField = orderedWalkthroughDocuments.find(document => {
    const isNotCurrentDoc = document.id !== currentDocumentId;
    const hasIncompleteField = document.requesterFields.some(({ isComplete }) => !isComplete);
    return isNotCurrentDoc && hasIncompleteField;
  });
  if (nextDocWithIncompleteField)
    return { documentId: nextDocWithIncompleteField.id, type: 'navigateToDocument' };

  const requesterCompletedAllFields =
    requesterFieldCounts.total > 0 && requesterFieldCounts.completed === requesterFieldCounts.total;
  if (requesterCompletedAllFields) return { type: 'none' };

  /**
   * A total of zero means the signer's field list hasn't resolved yet — an
   * empty list also makes `isUserFinishedWithCurrentDoc` vacuously true, which
   * is what routed us here. Tapping Next before the walkthrough knows what to
   * do is a no-op, not a dead end.
   */
  if (requesterFieldCounts.total === 0) return { type: 'none' };

  return { type: 'noNextDocument' };
}
